import { createTransform, persistReducer } from 'redux-persist';
import AsyncStorage from '@react-native-async-storage/async-storage';
import authSlice from './slices/authSlice';
import adminUserReducer from './slices/admin/adminUserSlice';
import organizationReducer from './slices/admin/organizationSlice';
import propertyReducer from './slices/admin/propertySlice';

// Persist configuration for auth slice
const authPersistConfig = {
  key: 'auth',
  storage: AsyncStorage,
  // Unified Auth (RES-89): `availableContexts` is authoritative server data from
  // the last login, so it is persisted and restored directly on relaunch (no
  // client re-derivation, no /me round-trip). `activeContext` is deliberately
  // NOT persisted, so the picker re-fires each session for multi-context
  // accounts and a single-context session is re-selected on restore.
  whitelist: ['user', 'isAuthenticated', 'biometricEnabled', 'availableContexts'],
  blacklist: ['isLoading', 'error'], // Don't persist loading/error states
};

// Create persisted auth reducer
export const persistedAuthReducer = persistReducer(authPersistConfig, authSlice);

// Strip nested objects from organizations/properties/roles before writing to
// localStorage. The full data stays in the in-memory Redux store; only the
// minimal fields needed to render selectors on reload are persisted.
const slimAdminUserTransform = createTransform(
  (inboundState: any, key) => {
    if (key === 'organizations') {
      return inboundState?.map(({ id, name }: any) => ({ id, name }));
    }
    if (key === 'properties') {
      return inboundState?.map(({ id, marketingName, organizationId }: any) => ({
        id, marketingName, organizationId,
      }));
    }
    if (key === 'roles') {
      return inboundState?.map(({ id, name, organizationId, propertyId }: any) => ({
        id, name, organizationId, propertyId,
      }));
    }
    return inboundState;
  },
  undefined,
  { whitelist: ['organizations', 'properties', 'roles'] },
);

// Persist configuration for adminUser slice (ensure property selection survives reload)
const adminUserPersistConfig = {
  key: 'adminUser',
  storage: AsyncStorage,
  transforms: [slimAdminUserTransform],
  whitelist: [
    'id',
    'email',
    'firstName',
    'lastName',
    'isSuperUser',
    'organizations',
    'properties',
    'selectedOrganizationId',
    'selectedPropertyId',
    'roles',
  ],
  blacklist: ['isLoading', 'emailCode', 'accessToken'],
};

export const persistedAdminUserReducer = persistReducer(adminUserPersistConfig, adminUserReducer);

// Persist configuration for organization slice (matching admin-web)
const organizationPersistConfig = {
  key: 'organization',
  version: 1,
  storage: AsyncStorage,
};

export const persistedOrganizationReducer = persistReducer(organizationPersistConfig, organizationReducer);

// Persist configuration for property slice (matching admin-web)
const propertyPersistConfig = {
  key: 'property',
  version: 1,
  storage: AsyncStorage,
};

export const persistedPropertyReducer = persistReducer(propertyPersistConfig, propertyReducer);

// Export the configuration for use in store
export { authPersistConfig };
